import { useMemo, useState } from 'react';
import { CalendarCheck, CalendarClock, MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import ScheduleBlockDialog, {
  type ClassOption,
  type ScheduleBlockValue,
  type StaffOption,
  type StudentOption,
  type TermOption,
} from './ScheduleBlockDialog';
import { WEEKDAY_LABELS } from './scheduleUtils';
import { useClassroomsQuery } from './useClassroomOperations';
import { useDeleteScheduleBlock } from './useScheduleBlocks';

interface Props {
  schoolId: string;
  schoolYearId: string;
  schoolYearStart: string;
  schoolYearEnd: string;
  classOffering: ClassOption;
  classOfferings: ReadonlyArray<ClassOption>;
  blocks: ReadonlyArray<ScheduleBlockValue>;
  staff: ReadonlyArray<StaffOption>;
  students: ReadonlyArray<StudentOption>;
  terms: ReadonlyArray<TermOption>;
  gradeLevels: ReadonlyArray<string>;
  schoolSlug: string;
  yearSlug: string;
  canManage: boolean;
  onChanged: () => void | Promise<void>;
}

function blockPattern(block: ScheduleBlockValue) {
  if (block.oneTime) return block.oneTimeDate ?? 'One-time event';
  return block.weekdays.map((day) => WEEKDAY_LABELS[day as keyof typeof WEEKDAY_LABELS]).join(', ');
}

function classDefaultDate(classOffering: ClassOption, schoolYearStart: string, schoolYearEnd: string) {
  const termStart = classOffering.term.startsOn ?? schoolYearStart;
  const termEnd = classOffering.term.endsOn ?? schoolYearEnd;
  const startsOn = termStart > schoolYearStart ? termStart : schoolYearStart;
  const endsOn = termEnd < schoolYearEnd ? termEnd : schoolYearEnd;
  return startsOn <= endsOn ? startsOn : schoolYearStart;
}

export default function ClassScheduleSection({
  schoolId,
  schoolYearId,
  schoolYearStart,
  schoolYearEnd,
  classOffering,
  classOfferings,
  blocks,
  staff,
  students,
  terms,
  gradeLevels,
  schoolSlug,
  yearSlug,
  canManage,
  onChanged,
}: Props) {
  const { data: classroomData, refetch: refetchClassrooms } = useClassroomsQuery(schoolId, !canManage);
  const [deleteBlock] = useDeleteScheduleBlock();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ScheduleBlockValue | null>(null);
  const [deleting, setDeleting] = useState<ScheduleBlockValue | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const classrooms = classroomData?.school?.classrooms ?? [];
  const sortedBlocks = useMemo(
    () => [...blocks].sort((a, b) => `${a.startsAt}-${a.displayTitle}`.localeCompare(`${b.startsAt}-${b.displayTitle}`)),
    [blocks],
  );
  const availableClasses = useMemo(() => {
    const byId = new Map(classOfferings.map((item) => [item.id, item]));
    byId.set(classOffering.id, classOffering);
    return [...byId.values()];
  }, [classOffering, classOfferings]);

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteError(null);
    setRefreshError(null);
    let result;
    try {
      result = await deleteBlock({ variables: { input: { id: deleting.id } } });
    } catch (deleteFailure) {
      const detail = deleteFailure instanceof Error && deleteFailure.message ? `: ${deleteFailure.message}` : '';
      setDeleteError(`Unable to delete this schedule block${detail}`);
      return;
    }

    const errors = result.data?.deleteScheduleBlock?.userErrors ?? [];
    if (errors.length > 0) {
      setDeleteError(errors.map((item) => item.message).join(', '));
      return;
    }
    setDeleting(null);
    try {
      await onChanged();
    } catch (refreshFailure) {
      const detail = refreshFailure instanceof Error && refreshFailure.message ? `: ${refreshFailure.message}` : '';
      setRefreshError(`Schedule block deleted, but the class schedule could not be refreshed${detail}. Reload the page to see the saved changes.`);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2"><CalendarClock className="h-5 w-5" /> Schedule</CardTitle>
            <CardDescription>Every block that uses this class roster. Multiple days can share one block.</CardDescription>
          </div>
          {canManage && <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }}><Plus className="mr-1 h-4 w-4" /> Add to schedule</Button>}
        </div>
      </CardHeader>
      <CardContent>
        {refreshError && <div role="alert" className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">{refreshError}</div>}
        {sortedBlocks.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">Schedule not assigned.</div>
        ) : (
          <div className="divide-y rounded-md border">
            {sortedBlocks.map((block) => (
              <div key={block.id} className="flex items-center justify-between gap-3 p-3">
                <div>
                  <p className="font-medium">{block.title || classOffering.displayName}</p>
                  <p className="text-sm text-muted-foreground">{blockPattern(block)} · {block.timeLabel}</p>
                  <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {block.classroom?.name ?? 'Location not assigned'}</p>
                </div>
                <div className="flex gap-1">{block.attendanceEnabled && <Button asChild variant="outline" size="sm"><Link to={`/app/${schoolSlug}/years/${yearSlug}/attendance/${block.id}`}><CalendarCheck className="mr-1 h-4 w-4" /> Attendance</Link></Button>}{canManage && <><Button variant="ghost" size="icon" aria-label={`Edit ${block.displayTitle}`} onClick={() => { setEditing(block); setDialogOpen(true); }}><Pencil className="h-4 w-4" /></Button><Button variant="ghost" size="icon" aria-label={`Delete ${block.displayTitle}`} onClick={() => setDeleting(block)}><Trash2 className="h-4 w-4 text-destructive" /></Button></>}</div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <ScheduleBlockDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        schoolYearId={schoolYearId}
        schoolId={schoolId}
        schoolYearStart={schoolYearStart}
        schoolYearEnd={schoolYearEnd}
        defaultDate={classDefaultDate(classOffering, schoolYearStart, schoolYearEnd)}
        defaultClassOfferingId={classOffering.id}
        classrooms={classrooms}
        staff={staff}
        students={students}
        classes={availableClasses}
        terms={terms}
        gradeLevels={gradeLevels}
        configureTermsHref={`/app/${schoolSlug}/settings?tab=school-years&year=${yearSlug}#academic-terms`}
        block={editing}
        onComplete={onChanged}
        onRefreshError={setRefreshError}
        onLocationCreated={async () => { await refetchClassrooms(); }}
      />

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this schedule block?</AlertDialogTitle><AlertDialogDescription>{deleting?.displayTitle} will be removed from the schedule. The academic class and roster remain intact.</AlertDialogDescription></AlertDialogHeader>
          {deleteError && <div className="text-sm text-destructive">{deleteError}</div>}
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={(event) => { event.preventDefault(); void handleDelete(); }}>Delete block</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
